import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  makeStyles,
} from '@material-ui/core';
import { ConnectApi, ConnectPatientConsent } from 'hydra-data-types';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { isUserPaid, updatePatientConsent } from '../../api/AuthApi';
import { linkPatientToHcp } from '../../api/LinkPatientToHcp';
import { getSubscriptionStatus } from '../../api/RevenueCat';
import nervaLogoWithName from '../../assets/svgs/logos/nerva-logo-with-name.svg';
import { AlertBar } from '../../components/AlertBar';
import { Loader } from '../../components/LoaderComponent';
import { USER_TYPE } from '../../constants';
import { Analytics } from '../../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../../helpers/analytics/AnalyticsEvent';
import { useAuthStore } from '../../store/AuthStore';
import { colors } from '../../theme/colors';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    paddingBottom: '20px',
    paddingTop: `${theme.spacing(3)}px`,
    [theme.breakpoints.up('sm')]: {
      paddingTop: `${theme.spacing(8)}px`,
    },
  },
  card: {
    width: '100%',
    height: '100%',
    borderRadius: 30,
    padding: theme.spacing(3),
    '& .MuiCardHeader-title': { textAlign: 'center' },
  },
  cardGrid: { width: '100%' },
  listItemRoot: {
    paddingLeft: 0,
  },
  dot: {
    height: '7px',
    /** the width only works if the aspect ratio is not supported by old browsers */
    width: '12px',
    aspectRatio: '1/1',
    borderRadius: '50%',
    backgroundColor: colors.green,
    marginRight: '5px',
  },
  buttons: {
    marginTop: '40px',
    display: 'flex',
    gap: '20px',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      alignItems: 'center',
    },
  },
}));

export const HCP_ACCESSING_REFERRAL_LINK = 'hcpLoggedInWithReferralLink';
export const PatientConsentScreen: React.FunctionComponent = () => {
  const classes = useStyles();
  const [isSubmittingAgree, setIsSubmittingAgree] = useState(false);
  const [isSubmittingDisagree, setIsSubmittingDisagree] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [linkingPatientToHcp, setLinkingPatientToHcp] = useState(false);
  const [hcp, setHcp] = useState<ConnectApi.ConnectUserAndProfile>();
  const history = useHistory();
  const hcpReferralHash = useAuthStore((state) => state.hcpReferralHash);
  const loggedInUserId = useAuthStore((state) => state.firebaseUser?.uid);
  const type = useAuthStore((state) => state.referralUrlType);

  useEffect(
    function runLinkPatientToHcp() {
      setLinkingPatientToHcp(true);
      async function runLink() {
        try {
          const hcpUser = await linkPatientToHcp({
            hcpReferralHash,
          });

          Analytics.track({
            event: AnalyticsEventName.h_referral_linked,
            referral_source: USER_TYPE.HCP,
            linked_with: hcpReferralHash,
          });

          setHcp(hcpUser);
          setLinkingPatientToHcp(false);
        } catch (error) {
          Analytics.track({
            event: AnalyticsEventName.h_referral_url_failed,
            type,
            error: String(error),
          });
          history.push({
            pathname: '/auth/sign-in-or-register',
            state: HCP_ACCESSING_REFERRAL_LINK,
          });
          setLinkingPatientToHcp(false);
        }
      }

      runLink();
    },
    [hcpReferralHash, loggedInUserId, history]
  );

  async function agree(consent: ConnectPatientConsent) {
    try {
      setIsSubmittingAgree(true);
      await updatePatientConsent(consent);

      const isSubscribed =
        (await getSubscriptionStatus(loggedInUserId ?? '')) ||
        (await isUserPaid(loggedInUserId ?? ''));

      setIsSubmittingAgree(false);

      Analytics.track({ event: AnalyticsEventName.a_link_consent_agreed });

      history.push({
        pathname: '/users/patient/thank-you',
        state: { isSubscribed, agreementStatus: true },
      });
    } catch (error) {
      setOpenAlert(true);
      setIsSubmittingAgree(false);
      Analytics.track({
        event: AnalyticsEventName.a_link_consent_failed,
        error: String(error),
      });
    }
  }

  async function disagree() {
    try {
      setIsSubmittingDisagree(true);
      const isSubscribed =
        (await getSubscriptionStatus(loggedInUserId ?? '')) ||
        (await isUserPaid(loggedInUserId ?? ''));

      setIsSubmittingDisagree(false);

      Analytics.track({ event: AnalyticsEventName.a_link_consent_skipped });

      history.push({
        pathname: '/users/patient/thank-you',
        state: { isSubscribed, agreementStatus: false },
      });
    } catch (error) {
      setOpenAlert(true);
      setIsSubmittingDisagree(false);
      Analytics.track({
        event: AnalyticsEventName.a_link_consent_failed,
        error: String(error),
      });
    }
  }

  return linkingPatientToHcp ? (
    <Loader />
  ) : (
    <Container maxWidth="sm">
      <AlertBar
        copy="Something went wrong! We cannot submit your request. Please contact your healthcare practitioner."
        status="error"
        open={openAlert}
        onClose={setOpenAlert}
      />
      <Grid container direction="column" alignItems="center" classes={{ root: classes.root }}>
        <Grid container item direction="column" alignItems="center">
          <Box marginBottom="20px">
            <img src={nervaLogoWithName} alt="mindset health logo" width={220} height={93} />
          </Box>
        </Grid>
        <Grid container item classes={{ root: classes.cardGrid }} direction="column">
          <Card classes={{ root: classes.card }} elevation={2}>
            <CardHeader title="Consent form" />
            <CardContent>
              <List>
                <ListItem classes={{ root: classes.listItemRoot }}>
                  <Box display="flex" alignItems="baseline">
                    <Box className={classes.dot} />
                    <ListItemText
                      primary={`Nerva (Mindset Health) can communicate 
                    with ${hcp?.name} ${hcp?.lastName} (${hcp?.email}) associated with this link.`}
                    />
                  </Box>
                </ListItem>
                <ListItem classes={{ root: classes.listItemRoot }}>
                  <Box display="flex" alignItems="baseline">
                    <Box className={classes.dot} />
                    <ListItemText
                      primary="Purpose of sharing information is to keep your
													healthcare provider informed, allowing them to stay in the loop
													and provide more accurate care."
                    />
                  </Box>
                </ListItem>
                <ListItem classes={{ root: classes.listItemRoot }}>
                  <Box display="flex" alignItems="baseline">
                    <Box className={classes.dot} />
                    <ListItemText
                      primary="Information communicated may include,
													but is not limited to, your name, app progress, engagement, 
													symptoms/results, reported history and demographics."
                    />
                  </Box>
                </ListItem>
                <ListItem classes={{ root: classes.listItemRoot }}>
                  <Box display="flex" alignItems="baseline">
                    <Box className={classes.dot} />
                    <ListItemText
                      primary="Medium of communication may include,
													but is not limited to, email, mail, phone call, fax, 
													and the Mindset Connect clinician platform."
                    />
                  </Box>
                </ListItem>
                <ListItem classes={{ root: classes.listItemRoot }}>
                  <Box display="flex" alignItems="baseline">
                    <Box className={classes.dot} />
                    <ListItemText
                      primary="Information will be kept confidential
													by referencing the provider by name, and using the details
													that you provide or they signed up with."
                    />
                  </Box>
                </ListItem>
                <ListItem classes={{ root: classes.listItemRoot }}>
                  <Box display="flex" alignItems="baseline">
                    <Box className={classes.dot} />
                    <ListItemText
                      primary="Authorization to communicate with your healthcare team
												about you begins after you agree below and will 
												end when revoked by you."
                    />
                  </Box>
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Box className={classes.buttons}>
            <Box width="200px" display="flex" position="relative">
              <Button
                type="submit"
                role="button"
                variant="contained"
                color="default"
                size="small"
                fullWidth
                disabled={isSubmittingDisagree || isSubmittingAgree}
                onClick={disagree}
              >
                <Box color="white">I don&apos;t agree</Box>
              </Button>
              {isSubmittingDisagree && <CircularProgress size={24} />}
            </Box>
            <Box width="200px" display="flex" position="relative">
              <Button
                type="submit"
                role="button"
                variant="contained"
                color="primary"
                size="small"
                disabled={isSubmittingAgree || isSubmittingDisagree}
                fullWidth
                onClick={() => {
                  agree({
                    hcpId: hcp?.userId,
                    patientConsent: true,
                  } as ConnectPatientConsent);
                }}
              >
                I agree
              </Button>
              {isSubmittingAgree && <CircularProgress size={24} />}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};
